import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';

/**
 * Compact metric card for a page header — a small donut with the total in the center and a short
 * title. Sized to sit inline next to the page title so the metrics use the header row instead of
 * a separate band below it. Click opens the full breakdown in a MetricDashboardDialog (rendered
 * by the caller — this component only reports the click).
 *
 * Props:
 *   title     — short label (e.g. "Status")
 *   data      — [{ label, value }]
 *   colorMap  — { [label]: hexColor }
 *   isLoading — while true, renders a skeleton placeholder instead of an empty/zero donut
 *   onClick   — optional; when provided, the card is clickable and shows a pointer cursor
 */
export const CompactMetricCard = ({ title, data, colorMap = {}, isLoading = false, onClick }) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(0,0,0,0.06)',
          borderRadius: 2, px: 1.25, py: 0.5,
        }}
      >
        <Skeleton variant="circular" width={54} height={54} sx={{ flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, pr: 0.5 }}>
          <Skeleton variant="text" width={70} sx={{ fontSize: 11 }} />
          <Skeleton variant="text" width={50} sx={{ fontSize: 10 }} />
        </Box>
      </Box>
    );
  }

  const total = (data || []).reduce((sum, item) => sum + item.value, 0);
  if (total === 0) {
    return null;
  }

  const chartData = (data || []).map((item, index) => ({
    id: index,
    value: item.value,
    label: item.label,
    color: colorMap[item.label] || '#c7c7c7',
  }));
  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: 'rgba(255,255,255,0.72)', border: '1px solid rgba(0,0,0,0.06)',
        borderRadius: 2, px: 1.25, py: 0.5, cursor: onClick ? 'pointer' : 'default',
        transition: 'background-color 0.15s ease',
        ...(onClick ? { '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' } } : {}),
      }}
    >
      <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
        <PieChart
          series={[{ data: chartData, innerRadius: 17, outerRadius: 26, paddingAngle: 2, cornerRadius: 2 }]}
          width={54}
          height={54}
          slots={{ legend: () => null }}
          slotProps={{ legend: { hidden: true } }}
          margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
        />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2c2a1e', lineHeight: 1 }}>{total}</Typography>
        </Box>
      </Box>
      <Box sx={{ minWidth: 0, pr: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#2c2a1e', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{title}</Typography>
        <Typography sx={{ fontSize: 10, color: '#6b6a52', lineHeight: 1.2, whiteSpace: 'nowrap' }}>{total} total</Typography>
      </Box>
    </Box>
  );
};

export default CompactMetricCard;
